import { useCallback, useEffect, useState } from 'react';
import { ApiClientError } from '@techsofit/app-kit';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Minimal fetch-on-mount helper (the app intentionally has no data-fetching
 * library yet). Re-runs when `deps` change; `reload()` forces a refetch.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    run()
      .then((res) => active && setData(res))
      .catch((err) => {
        if (!active) return;
        setError(err instanceof ApiClientError ? err.message : 'Could not load data.');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [run, nonce]);

  return { data, loading, error, reload: () => setNonce((n) => n + 1) };
}
